import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { apiService } from "@/services/apiService";
import { useAuthStore } from "@/stores/auth";
import { PERMISSIONS } from "@/constants/permissions";

export function useTable() {
  const { t } = useI18n();
  const { hasScope } = useAuthStore();

  const error = ref(null);
  const loading = ref(false);
  const tables = ref([]);

  const canReadTable = ref(hasScope(PERMISSIONS.DINING_TABLE_READ));

  const fetchTableById = async (id) => {
    if (!canReadTable.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "tables",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      return await apiService.get(`/restaurant_tables/${id}`);
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "table" });
      return null;
    } finally {
      loading.value = false;
    }
  };

  const fetchTables = async (skip = 0, limit = 100) => {
    if (!canReadTable.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "tables",
      });
      error.value = errorObject.detail;
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      const response = await apiService.get("/restaurant_tables/", {
        skip,
        limit,
      });
      tables.value = Array.isArray(response?.items)
        ? response.items
        : response || [];
      return tables.value;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "tables" });
      tables.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  };

  return {
    error,
    fetchTableById,
    fetchTables,
    loading,
    tables,
  };
}
